import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not logged in
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Try httpOnly cookie first (sent automatically with withCredentials),
    // fall back to localStorage token as secondary mechanism
    try {
      const token = localStorage.getItem("userToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/auth/me`, { headers, withCredentials: true });
      setUser(res.data.user);
    } catch {
      localStorage.removeItem("userToken");
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // Store token as fallback; httpOnly cookie is the primary auth mechanism
    if (res.data.token) localStorage.setItem("userToken", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data, { withCredentials: true });
    if (res.data.token) localStorage.setItem("userToken", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    localStorage.removeItem("userToken");
    setUser(false);
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* expected */ }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("userToken");
    return {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    };
  };

  const loginWithPiToken = (userData, token) => {
    if (token) localStorage.setItem("userToken", token);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, getAuthHeaders, checkAuth, loginWithPiToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
